import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  VStack,
  Heading,
  Box,
  Checkbox,
  Divider,
  HStack,
} from '@chakra-ui/react';

import { UserMutation } from '../models';
import { ChangeEvent, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTypedSelector } from '../../../hooks';
import {
  useGetAllRolesQuery,
  useReassignTeamMemberRoleMutation,
} from '../../../services';
import { RolesMenu } from '../../roles/components';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const UpdateUsersModal = ({
  isOpen,
  onClose,
  users,
}: {
  isOpen: boolean;
  onClose: () => void;
  users: UserMutation[];
}) => {
  const [isDisabledCheck, setIsDisabledCheck] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState(0);
  const { userDetails } = useTypedSelector(state => state.auth);

  const { control, getValues } = useForm<{
    rolesPerUsers: { teamMemberId: number; roleId: number }[];
  }>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rolesPerUsers',
  });

  const { data: allRoles } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails,
    }
  );

  const [reassignTeamMemberRole, { isLoading: isReassignLoading }] =
    useReassignTeamMemberRoleMutation();

  const handleUpdateUsers = async () => {
    const response = await reassignTeamMemberRole(getValues('rolesPerUsers'));

    // A rejected reassignment used to close the modal as if the new roles had
    // been applied. Keep it open with the selections intact instead.
    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not update the selected users',
        description:
          'No role was changed. Please review your selection and try again.',
      });
      return;
    }

    onClose();
  };

  const handleChangeRolePerUser = (roleId: number, teamMemberId: number) => {
    const rolePerUserIndex = getValues('rolesPerUsers').findIndex(
      roleByUser => roleByUser.teamMemberId === teamMemberId
    );

    if (rolePerUserIndex) remove(rolePerUserIndex);
    append({ roleId, teamMemberId });
  };

  const handelCheckAllUsers = (e: ChangeEvent<HTMLInputElement>) => {
    remove();
    if (e.target.checked)
      users.forEach(({ id }) =>
        append({ roleId: selectedRoleId, teamMemberId: id })
      );
  };

  return (
    <>
      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" color="custom_blue.1">
              <Heading fontSize="md" fontWeight="semibold">
                select a new role
              </Heading>
              <Text fontSize="sm">
                You are about to select a new role for the following users :
              </Text>

              {allRoles?.data.content && (
                <Box>
                  {users.map(({ id: teamMemberId, name }) => (
                    <HStack key={teamMemberId} justify="space-between" w="full">
                      <Text fontSize="xs" color="custom_blue.1">
                        {name}
                      </Text>

                      {allRoles.data.content && (
                        <RolesMenu
                          roles={allRoles.data.content}
                          onChangeMenuItem={roleId =>
                            handleChangeRolePerUser(roleId, teamMemberId)
                          }
                        />
                      )}
                    </HStack>
                  ))}

                  <HStack>
                    <HStack>
                      <Checkbox
                        isDisabled={isDisabledCheck}
                        colorScheme="orange"
                        onChange={handelCheckAllUsers}
                      />

                      <Text fontSize="sm" color="custom_blue.1">
                        Select All
                      </Text>
                    </HStack>

                    <RolesMenu
                      roles={allRoles.data.content}
                      onChangeMenuItem={roleId => {
                        setIsDisabledCheck(false);
                        setSelectedRoleId(roleId);
                      }}
                    />
                  </HStack>
                  <Divider />
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter display="flex" flexDir="column" gap="4">
            <Button
              bg="error.600"
              _hover={{ bg: 'error.600' }}
              color="white"
              w="full"
              onClick={handleUpdateUsers}
              isDisabled={!fields.length}
              isLoading={isReassignLoading}
            >
              Update
            </Button>
            <Button
              onClick={onClose}
              w="full"
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
